import React, { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Upload, CheckCircle, XCircle, Pencil, Trash2, Search, Globe, Building2 } from "lucide-react";
import { adminMemoryService } from "../services/admin-memory.service";
import { clientService, Client } from "../services/client.service";
import type { Memory, MemoryStatus } from "../services/memory.service";
import Pagination from "../components/Pagination";
import CreateAdminKnowledgeModal from "../components/admin/CreateAdminKnowledgeModal";
import EditAdminKnowledgeModal from "../components/admin/EditAdminKnowledgeModal";
import DeleteAdminKnowledgeModal from "../components/admin/DeleteAdminKnowledgeModal";
import ImportAdminKnowledgeModal from "../components/admin/ImportAdminKnowledgeModal";
import { useToast } from "../context/ToastContext";
import "../styles/clients.css";

export default function AdminKnowledgeBasePage() {
    const [entries, setEntries] = useState<Memory[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [statusFilter, setStatusFilter] = useState<MemoryStatus | "">("");
    const [scopeFilter, setScopeFilter] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [searchActive, setSearchActive] = useState(false);

    // Pagination
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [showCreate, setShowCreate] = useState(false);
    const [editEntry, setEditEntry] = useState<Memory | null>(null);
    const [deleteEntry, setDeleteEntry] = useState<Memory | null>(null);
    const [showImport, setShowImport] = useState(false);
    const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [bulkLoading, setBulkLoading] = useState(false);
    const { showToast } = useToast();

    const selectAllRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate =
                selectedIds.size > 0 && selectedIds.size < entries.length;
        }
    }, [selectedIds.size, entries.length]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setPage(1);
    }, [statusFilter, scopeFilter]);

    // Load clients list for scope filter dropdown
    useEffect(() => {
        clientService.getAll().then(setClients).catch(() => {});
    }, []);

    const fetchEntries = useCallback(async () => {
        setLoading(true);
        setError("");
        setSelectedIds(new Set());
        try {
            let data: Memory[];
            let meta = { page: 1, limit: 20, total: 0, totalPages: 0 };

            if (searchActive && searchQuery.trim()) {
                // Search endpoint is not paginated
                data = await adminMemoryService.search({
                    query: searchQuery.trim(),
                    status: statusFilter || undefined,
                    clientId: scopeFilter || undefined,
                });
                meta = { page: 1, limit: data.length || 20, total: data.length, totalPages: 1 };
            } else {
                const res = await adminMemoryService.list({
                    status: statusFilter || undefined,
                    clientId: scopeFilter || undefined,
                    page,
                    limit,
                });
                data = res.data;
                meta = res.meta;
            }
            setEntries(data);
            setTotal(meta.total);
            setTotalPages(meta.totalPages);
        } catch (err: any) {
            if (err.response?.status === 403) {
                setError("Access denied. Admin privileges required.");
            } else {
                setError(err.response?.data?.msg || "Failed to load knowledge base.");
            }
        } finally {
            setLoading(false);
        }
    }, [statusFilter, scopeFilter, searchActive, searchQuery, page, limit]);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries]);

    const handlePageChange = (p: number) => {
        setPage(Math.max(1, Math.min(p, totalPages || 1)));
    };

    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            setSearchActive(true);
            setPage(1);
        }
    };

    const clearSearch = () => {
        setSearchQuery("");
        setSearchActive(false);
        setPage(1);
    };

    const handleStatusToggle = async (entry: Memory) => {
        const newStatus: MemoryStatus = entry.status === "DRAFT" ? "APPROVED" : "DRAFT";
        try {
            await adminMemoryService.updateStatus(entry.id, newStatus);
            fetchEntries();
        } catch (err: any) {
            setError(err.response?.data?.msg || "Failed to update status.");
        }
    };

    const handleBulkStatus = async (status: MemoryStatus) => {
        if (selectedIds.size === 0) return;
        setBulkLoading(true);
        setError("");
        try {
            const result = await adminMemoryService.bulkUpdateStatus(Array.from(selectedIds), status);
            let msg = `${result.updated} entries ${status === "APPROVED" ? "approved" : "set as draft"}`;
            if (result.notFound?.length > 0) {
                msg += `. ${result.notFound.length} not found (may have been deleted).`;
            }
            showToast(msg);
            fetchEntries();
        } catch (err: any) {
            setError(err.response?.data?.msg || "Failed to update statuses.");
        } finally {
            setBulkLoading(false);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        setBulkDeleteLoading(true);
        setError("");
        try {
            const result = await adminMemoryService.bulkDelete(Array.from(selectedIds));
            let msg = `${result.deleted} entries deleted`;
            if (result.notFound?.length > 0) {
                msg += `. ${result.notFound.length} not found.`;
            }
            showToast(msg);
            setBulkDeleteConfirm(false);
            fetchEntries();
        } catch (err: any) {
            setError(err.response?.data?.message || err.response?.data?.msg || "Failed to delete entries.");
        } finally {
            setBulkDeleteLoading(false);
        }
    };

    const toggleSelect = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === entries.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(entries.map((e) => e.id)));
        }
    };

    const truncate = (text: string, max = 100) =>
        text.length > max ? text.slice(0, max) + "..." : text;

    const getScope = (entry: Memory) => {
        if (entry.metadata?.clientId) {
            const client = clients.find((c) => c.id === entry.metadata?.clientId);
            return { label: client?.name || "Client", isGlobal: false };
        }
        return { label: "Global", isGlobal: true };
    };

    const formatDate = (iso: string) => {
        const d = new Date(iso);
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    return (
        <>
            <div className="clients-container admin-kb-container">
                <div className="clients-header">
                    <h1>Global Knowledge Base</h1>
                </div>

                {/* Filter bar */}
                <div className="kb-filter-bar">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as MemoryStatus | "")}
                    >
                        <option value="">All statuses</option>
                        <option value="DRAFT">Draft</option>
                        <option value="APPROVED">Approved</option>
                    </select>

                    <select
                        value={scopeFilter}
                        onChange={(e) => setScopeFilter(e.target.value)}
                    >
                        <option value="">All scopes</option>
                        <option value="global">Global only</option>
                        {clients.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>

                    <form className="admin-kb-search" onSubmit={handleSearch}>
                        <input
                            type="text"
                            placeholder="Search entries..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                if (!e.target.value.trim()) setSearchActive(false);
                            }}
                        />
                        <button type="submit" className="btn-action" title="Search">
                            <Search size={14} />
                        </button>
                        {searchActive && (
                            <button type="button" className="btn-action" onClick={clearSearch} title="Clear">
                                <XCircle size={14} />
                            </button>
                        )}
                    </form>

                    <div className="clients-header-actions">
                        <button className="btn-create" onClick={() => setShowCreate(true)}>
                            <Plus size={16} /> Add Knowledge
                        </button>
                        <button className="btn-create" onClick={() => setShowImport(true)}>
                            <Upload size={16} /> Import
                        </button>
                    </div>
                </div>

                {error && <div className="modal-error" style={{ marginBottom: 16 }}>{error}</div>}

                {loading ? (
                    <div className="clients-loading">
                        <div className="auth-spinner" />
                    </div>
                ) : total === 0 ? (
                    <div className="clients-empty">
                        <h3>No knowledge entries found</h3>
                        <p>{searchActive ? "Try a different search term." : "Add global Q&A entries to power all chatbots."}</p>
                        {!searchActive && (
                            <button className="btn-create" onClick={() => setShowCreate(true)}>
                                <Plus size={16} /> Add Knowledge
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                    {selectedIds.size > 0 && (
                        <div className="bulk-action-bar">
                            <span>{selectedIds.size} selected</span>
                            <button className="btn-bulk approve" onClick={() => handleBulkStatus("APPROVED")} disabled={bulkLoading}>
                                <CheckCircle size={14} /> Approve
                            </button>
                            <button className="btn-bulk draft" onClick={() => handleBulkStatus("DRAFT")} disabled={bulkLoading}>
                                <XCircle size={14} /> Draft
                            </button>
                            <button className="btn-bulk delete" onClick={() => setBulkDeleteConfirm(true)} disabled={bulkLoading}>
                                <Trash2 size={14} /> Delete
                            </button>
                            <button className="btn-bulk clear" onClick={() => setSelectedIds(new Set())}>
                                Clear
                            </button>
                        </div>
                    )}
                    <div className="clients-table-card">
                        <table className="clients-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}>
                                        <input
                                            ref={selectAllRef}
                                            type="checkbox"
                                            checked={selectedIds.size === entries.length && entries.length > 0}
                                            onChange={toggleSelectAll}
                                            className="kb-row-checkbox"
                                        />
                                    </th>
                                    <th>Question</th>
                                    <th>Answer</th>
                                    <th>Category</th>
                                    <th>Scope</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((entry) => {
                                    const scope = getScope(entry);
                                    return (
                                        <tr key={entry.id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(entry.id)}
                                                    onChange={() => toggleSelect(entry.id)}
                                                    className="kb-row-checkbox"
                                                />
                                            </td>
                                            <td className="kb-cell-truncate" title={entry.question}>
                                                {truncate(entry.question)}
                                            </td>
                                            <td className="kb-cell-truncate" title={entry.answer}>
                                                {truncate(entry.answer)}
                                            </td>
                                            <td>
                                                {entry.metadata?.category ? (
                                                    <span className="client-type-badge">{entry.metadata.category}</span>
                                                ) : (
                                                    <span className="client-type-badge empty">—</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`kb-scope-badge ${scope.isGlobal ? "global" : "client"}`}>
                                                    {scope.isGlobal ? <Globe size={12} /> : <Building2 size={12} />}
                                                    {scope.label}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`kb-status-badge ${entry.status === "DRAFT" ? "draft" : "approved"}`}>
                                                    {entry.status}
                                                </span>
                                            </td>
                                            <td className="kb-cell-date">{formatDate(entry.createdAt)}</td>
                                            <td>
                                                <div className="client-actions">
                                                    <button className="btn-action" title={entry.status === "DRAFT" ? "Approve" : "Set as Draft"} onClick={() => handleStatusToggle(entry)}>
                                                        {entry.status === "DRAFT" ? <CheckCircle size={14} /> : <XCircle size={14} />}
                                                    </button>
                                                    <button className="btn-action" title="Edit" onClick={() => setEditEntry(entry)}>
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button className="btn-action danger" title="Delete" onClick={() => setDeleteEntry(entry)}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {!searchActive && (
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            total={total}
                            limit={limit}
                            onPageChange={handlePageChange}
                            onLimitChange={handleLimitChange}
                        />
                    )}
                    </>
                )}
            </div>

            {showCreate && (
                <CreateAdminKnowledgeModal
                    onClose={() => setShowCreate(false)}
                    onCreated={() => { setShowCreate(false); fetchEntries(); }}
                />
            )}
            {editEntry && (
                <EditAdminKnowledgeModal
                    entry={editEntry}
                    onClose={() => setEditEntry(null)}
                    onUpdated={() => { setEditEntry(null); fetchEntries(); }}
                />
            )}
            {deleteEntry && (
                <DeleteAdminKnowledgeModal
                    entry={deleteEntry}
                    onClose={() => setDeleteEntry(null)}
                    onDeleted={() => { setDeleteEntry(null); showToast("Entry deleted"); fetchEntries(); }}
                />
            )}
            {showImport && (
                <ImportAdminKnowledgeModal
                    onClose={() => setShowImport(false)}
                    onImported={() => fetchEntries()}
                />
            )}
            {bulkDeleteConfirm && (
                <div className="modal-overlay" onClick={() => setBulkDeleteConfirm(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h2>Confirm Delete</h2>
                        <p className="modal-subtitle">This action cannot be undone</p>
                        <div className="delete-warning">
                            Are you sure you want to delete <strong>{selectedIds.size}</strong> entries?
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="btn-modal-cancel" onClick={() => setBulkDeleteConfirm(false)}>Cancel</button>
                            <button type="button" className="btn-modal-danger" onClick={handleBulkDelete} disabled={bulkDeleteLoading}>
                                {bulkDeleteLoading ? "Deleting..." : `Delete ${selectedIds.size} entries`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
